/** Sugestões (v1): links da viagem — ver `apps/api/src/domain/suggestions.ts`. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const CATEGORIAS = ['passeio', 'comida', 'compras', 'hospedagem', 'transporte', 'outro'] as const;
export type Categoria = (typeof CATEGORIAS)[number];
export const CATEGORIA_ROTULO: Record<Categoria, string> = {
  passeio: 'Passeio',
  comida: 'Comida',
  compras: 'Compras',
  hospedagem: 'Hospedagem',
  transporte: 'Transporte',
  outro: 'Outro',
};

export interface Sugestao {
  id: string;
  url: string;
  category: Categoria;
  comment: string | null;
  provider: 'youtube' | 'tiktok' | 'instagram' | 'other';
  title: string | null;
  thumbnailUrl: string | null;
  authorName: string | null;
  host: string;
  createdBy: { memberId: string; name: string; me: boolean };
  createdAt: string;
  canDelete: boolean;
  /** Vínculo com o roteiro (PO, 22/09/2026). A tela desenha o que vem daqui. */
  linkStatus: 'none' | 'pending_link' | 'linked' | 'pending_unlink' | 'rejected';
  activity: AtividadeDaSugestao | null;
  requestedActivity: AtividadeDaSugestao | null;
  requestedBy: { memberId: string; name: string } | null;
  canDecide: boolean;
  canCancelRequest: boolean;
  canRequestLink: boolean;
  canRequestUnlink: boolean;
}

export interface AtividadeDaSugestao {
  id: string;
  title: string;
  date: string;
  startTime: string;
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

export const sugestoesApi = {
  listar: (tripId: string) => pedir<{ suggestions: Sugestao[] }>(`/api/v1/trips/${tripId}/suggestions`),
  criar: (tripId: string, input: { id: string; url: string; category: Categoria; comment?: string | null }) =>
    pedir<{ suggestion: Sugestao }>(`/api/v1/trips/${tripId}/suggestions`, { method: 'POST', body: JSON.stringify(input) }),
  apagar: (tripId: string, id: string) => pedir<{ ok: true }>(`/api/v1/trips/${tripId}/suggestions/${id}`, { method: 'DELETE' }),
  // Vínculo com o roteiro: o autor pede, quem administra decide (e faz direto).
  vincular: (tripId: string, id: string, activityId: string) =>
    pedir<{ suggestion: Sugestao }>(`/api/v1/trips/${tripId}/suggestions/${id}/link`, { method: 'POST', body: JSON.stringify({ activityId }) }),
  desvincular: (tripId: string, id: string) => pedir<{ suggestion: Sugestao }>(`/api/v1/trips/${tripId}/suggestions/${id}/link`, { method: 'DELETE' }),
  cancelarPedido: (tripId: string, id: string) => pedir<{ suggestion: Sugestao }>(`/api/v1/trips/${tripId}/suggestions/${id}/link-request`, { method: 'DELETE' }),
  decidir: (tripId: string, id: string, approve: boolean) =>
    pedir<{ suggestion: Sugestao }>(`/api/v1/trips/${tripId}/suggestions/${id}/link-decision`, { method: 'POST', body: JSON.stringify({ approve }) }),
  daAtividade: (tripId: string, activityId: string) => pedir<{ suggestions: Sugestao[] }>(`/api/v1/trips/${tripId}/activities/${activityId}/suggestions`),
  pendentes: (tripId: string) => pedir<{ pending: number }>(`/api/v1/trips/${tripId}/suggestions/pending-links`),
};

const chave = (tripId: string) => ['sugestoes', tripId];

export function useSugestoes(tripId: string) {
  return useQuery({ queryKey: chave(tripId), queryFn: () => sugestoesApi.listar(tripId), enabled: tripId.length > 0, staleTime: 15_000 });
}

export function useCriarSugestao(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; url: string; category: Categoria; comment?: string | null }) => sugestoesApi.criar(tripId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chave(tripId) }),
  });
}

export function useApagarSugestao(tripId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sugestoesApi.apagar(tripId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chave(tripId) }),
  });
}

export const PROVEDOR_ROTULO: Record<Sugestao['provider'], string> = { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', other: 'Link' };

/** Qualquer ação de vínculo mexe na lista, na atividade e na contagem do alerta. */
function invalidarVinculo(qc: ReturnType<typeof useQueryClient>, tripId: string) {
  void qc.invalidateQueries({ queryKey: chave(tripId) });
  void qc.invalidateQueries({ queryKey: ['sugestoes', tripId, 'atividade'] });
  void qc.invalidateQueries({ queryKey: ['sugestoes', tripId, 'pendentes'] });
  void qc.invalidateQueries({ queryKey: ['notificacoes', tripId] });
}

export function useVinculoDaSugestao(tripId: string) {
  const qc = useQueryClient();
  const comum = { onSuccess: () => invalidarVinculo(qc, tripId) };
  return {
    vincular: useMutation({ mutationFn: ({ id, activityId }: { id: string; activityId: string }) => sugestoesApi.vincular(tripId, id, activityId), ...comum }),
    desvincular: useMutation({ mutationFn: (id: string) => sugestoesApi.desvincular(tripId, id), ...comum }),
    cancelar: useMutation({ mutationFn: (id: string) => sugestoesApi.cancelarPedido(tripId, id), ...comum }),
    decidir: useMutation({ mutationFn: ({ id, approve }: { id: string; approve: boolean }) => sugestoesApi.decidir(tripId, id, approve), ...comum }),
  };
}

/** As sugestões vinculadas a uma atividade — bloco na tela da atividade. */
export function useSugestoesDaAtividade(tripId: string, activityId: string) {
  return useQuery({
    queryKey: ['sugestoes', tripId, 'atividade', activityId],
    queryFn: () => sugestoesApi.daAtividade(tripId, activityId),
    enabled: tripId.length > 0 && activityId.length > 0,
    staleTime: 15_000,
  });
}

/** Quantos pedidos esperam decisão (0 para quem não decide) — alerta da Home. */
export function usePedidosDeSugestao(tripId: string) {
  return useQuery({
    queryKey: ['sugestoes', tripId, 'pendentes'],
    queryFn: () => sugestoesApi.pendentes(tripId),
    enabled: tripId.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

